/**
 * EnhancedSelect Component
 * 
 * An enhanced version of react-select with additional features:
 * - Custom option rendering with metadata support
 * - Source-based icons (history, document, suggestion)
 * - Custom value creation
 * - Visual indicators for current trip items
 * - Accent-insensitive search
 * 
 * Built on top of react-select and react-select/creatable to provide
 * a consistent, feature-rich selection experience across the application.
 * 
 * Common Usage Patterns:
 * 1. Operator Selection (airlines, train operators, bus operators)
 * 2. City Selection (departure and arrival cities)
 * 3. Station Selection (for trains)
 * 
 * Key Features:
 * - Custom Value Support: Allows users to enter values not in the predefined options
 * - Metadata Display: Shows icons and indicators based on data source and status
 * - Consistent UX: Provides unified selection experience across all travel forms
 * - Smart Processing: Handles both predefined options and user-created custom values
 * 
 * Option Format:
 * {
 *   label: "Display text",
 *   value: "internal-value",
 *   meta: {
 *     isRecent: true,
 *     source: "document_extraction",
 *     inCurrentTrip: false
 *   }
 * }
 * 
 * Implementation Notes:
 * - Uses react-select for standard selection
 * - Uses react-select/creatable for custom value support
 * - Implements accent-insensitive search for better matching
 * - Provides visual feedback for focused and selected states
 * - Supports metadata-based icon display
 * 
 * @component
 */

import React from 'react';
import Select, { createFilter } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FaHistory, FaFileAlt, FaComment } from 'react-icons/fa';

/**
 * Custom Option Component with Metadata Display
 * 
 * Renders a select option with additional metadata indicators:
 * - Source icons (history, document, or suggestion)
 * - Visual highlighting for focused state
 * - Special styling for items in current trip
 * 
 * Used extensively in travel forms for:
 * - Displaying recently used operators and cities
 * - Indicating extracted data from travel documents
 * - Highlighting suggestions based on user history
 * - Showing items that are part of the current trip
 * 
 * Visual Indicators:
 * - History Icon: Previously selected items
 * - Document Icon: Data extracted from documents
 * - Comment Icon: System-generated suggestions
 * - Current Trip Highlight: Items used in active trip
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.innerProps - Props to be spread on the option element
 * @param {Object} props.data - Option data including label and metadata
 * @param {boolean} props.isFocused - Whether the option is currently focused
 * @param {Object} [props.data.meta] - Metadata for the option
 * @param {boolean} [props.data.meta.isRecent] - Whether this is a recent item
 * @param {string} [props.data.meta.source] - Source of the option (document_extraction, suggestion)
 * @param {boolean} [props.data.meta.inCurrentTrip] - Whether the item is in current trip
 */
const OptionWithMetadata = ({ innerProps, data, isFocused }) => {
  const { isRecent, source, inCurrentTrip } = data.meta || {};
    
  // Determine appropriate icon based on the source type
  let SourceIcon = null;
  if (isRecent) {
    if (source === 'document_extraction') {
      SourceIcon = FaFileAlt;          // Document icon for extracted data
    } else if (source === 'suggestion') {
      SourceIcon = FaComment;          // Comment icon for suggestions
    } else {
      SourceIcon = FaHistory;          // History icon for recent selections
    }
  }
    
  return (
    <div
      {...innerProps}
      className={`enhanced-option ${isFocused ? 'focused' : ''} ${inCurrentTrip ? 'current-trip' : ''}`}
    >
      <div className="option-content">
        <span className="option-label">{data.label}</span>
                
        {/* Render metadata section only for recent items */}
        {isRecent && (
          <div className="option-meta">
            {SourceIcon && <SourceIcon className="source-icon" />}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Enhanced Select Component
 * 
 * A wrapper around react-select that provides enhanced functionality:
 * - Custom option rendering with metadata
 * - Support for creating custom values
 * - Accent-insensitive search
 * - Consistent styling across the application
 * 
 * Travel Form Integration:
 * - Operator Selection:
 *   ```jsx
 *   <EnhancedSelect
 *     options={operatorOptions}
 *     onChange={(selected) => handleInputChange('operator', selected)}
 *     value={formData.operator}
 *     placeholder="Select or type any operator"
 *     isSearchable={true}
 *     allowCustomValues={true}
 *   />
 *   ```
 * 
 * - City Selection:
 *   ```jsx
 *   <EnhancedSelect
 *     options={cityOptions}
 *     onChange={(selected) => handleInputChange('departureCity', selected)}
 *     value={formData.departureCity}
 *     placeholder="Select or type any city name"
 *     isSearchable={true}
 *     allowCustomValues={true}
 *   />
 *   ```
 * 
 * Custom Value Handling:
 * - Creates standardized custom option objects
 * - Maintains consistent data structure with predefined options
 * - Supports metadata for custom entries
 * - Provides visual feedback for custom value usage
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.options - Array of select options
 * @param {Function} props.onChange - Change handler function
 * @param {Object} props.value - Currently selected value
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} [props.isSearchable=true] - Whether to enable search functionality
 * @param {boolean} [props.allowCustomValues=false] - Whether to allow creating custom values
 * @param {Object} props.props - Additional props to pass to react-select
 */
const EnhancedSelect = ({ 
  options, 
  onChange, 
  value, 
  placeholder, 
  isSearchable = true, 
  allowCustomValues = false,
  ...props 
}) => {
  /**
     * Formats the label for custom value creation
     * 
     * @param {string} inputValue - The user's input value
     * @returns {string} Formatted label for the create option
     */
  const formatCreateLabel = (inputValue) => `Use "${inputValue}"`;
    
  /**
     * Enhanced change handler that processes both selected and created options
     * 
     * @param {Object} selectedOption - The selected or created option
     * @param {Object} actionMeta - Metadata about the change action
     */
  const handleChange = (selectedOption, actionMeta) => {
    if (actionMeta.action === 'create-option') {
      // Transform created option into standard format
      const customOption = {
        label: selectedOption.value,
        value: selectedOption.value,
        name: selectedOption.value,
        isCustom: true
      };
      onChange(customOption);
    } else {
      onChange(selectedOption);
    }
  };
    
  /**
     * Common configuration for both Select and CreatableSelect
     * 
     * Includes:
     * - Custom option component
     * - Accent-insensitive filtering
     * - Consistent styling prefix
     * - Passed-through props
     */
  const commonProps = {
    options,
    onChange: handleChange,
    value,
    placeholder,
    isSearchable,
    components: {
      Option: OptionWithMetadata
    },
    classNamePrefix: 'enhanced-select',
    filterOption: createFilter({ ignoreAccents: true }),
    ...props
  };
    
  // Render appropriate select component based on allowCustomValues flag
  return allowCustomValues ? (
    <CreatableSelect
      {...commonProps}
      formatCreateLabel={formatCreateLabel}
    />
  ) : (
    <Select {...commonProps} />
  );
};

export default EnhancedSelect; 